/**
 * x402 redaction stack — bearer / payment signature / Cookie sanitisation for
 * logs, spans, error reports, and any other surface where a string
 * payload could leak a session token or a settlement payload to a
 * less-trusted consumer (Sentry, OTel collector, screen-share, etc.).
 *
 * Threat model (plan §5.3, Sec C1 CRITICAL, Codex B2):
 *   A buyer agent receives a 32-byte session token in the
 *   /x402/onboard response body once. The agent then uses it as
 *   `Authorization: Bearer <token>` on session-scoped calls. The
 *   token is a long-lived credential (24h sliding TTL, 7d hard
 *   ceiling). If it leaks into a captured log line, a span attribute,
 *   or an error report, anyone with read access to that downstream
 *   sink can resume the agent's session.
 *
 *   The same hazard applies to `PAYMENT-SIGNATURE:
 *   <base64-payment-payload>` / legacy `X-PAYMENT: ...` (carries the
 *   buyer's settlement-authorisation signature) and to `Cookie:`
 *   headers (which may carry NextAuth sessions).
 *
 * Design:
 *   - Pure functions (no side effects, no I/O). Trivially unit-testable.
 *   - Idempotent — safe to apply twice.
 *   - Operate on STRINGS, not structured objects. The logger / span
 *     pipelines may pass us arbitrary serialised text; we treat them
 *     all the same way.
 *   - Conservative replacement: redact the WHOLE value, not just the
 *     "looks-like-a-token" suffix. False positives on a literal that
 *     happens to match the bearer regex are cheap; false negatives
 *     that leak a real token are not.
 *
 * Wired into:
 *   - apps/aient-app/src/lib/logger.ts (every log line, every span
 *     attribute) — see `redactLoggerPayload` for the structured-
 *     attribute path.
 *   - OTel SDK fetch instrumentation header capture (deny-list at the
 *     SDK layer, defence in depth at the collector layer).
 */

/**
 * Match an `Authorization: Bearer <token>` header value (whole header
 * line or just the value) — covers both `Bearer abc` AND `bearer abc`
 * casing, and matches the 32-byte token's base64url representation
 * (43 chars without padding) ALONG WITH longer rotating-key variants
 * by allowing 32–256 token chars. The character class matches the
 * conservative base64url-safe alphabet.
 *
 * Anchored at a word boundary so we don't accidentally match a
 * substring inside a larger token name.
 *
 * Global flag is intentional — replace ALL occurrences. Callers MUST
 * use `String#replace` (which resets `lastIndex` per call) rather than
 * `regex.exec()` in a loop without resetting (which would skip every
 * other match due to lastIndex carryover).
 */
export const BEARER_REGEX = /(\bbearer\s+)[A-Za-z0-9_\-+/=.]{32,256}/gi

/**
 * Match the entire payment signature header value across both
 * plain-text (`PAYMENT-SIGNATURE: eyJ...`, `X-PAYMENT: eyJ...`) and
 * JSON-serialised (`"payment-signature": "eyJ..."`, `"x-payment":
 * "eyJ..."`) shapes. Header name + optional surrounding quotes + delimiter
 * (`:` or `=`) + optional whitespace + optional opening quote form
 * the capture-group-1 prefix; the value class is the conservative
 * base64url alphabet so we stop cleanly at JSON-closing quotes,
 * commas, braces, CRLF, etc.
 *
 * Header-name match is case-insensitive.
 */
export const PAYMENT_SIGNATURE_HEADER_REGEX = /(["']?\b(?:payment-signature|x-payment)["']?\s*[:=]\s*["']?)([A-Za-z0-9_\-+/=.]{8,4096})/gi

/**
 * Match a `Cookie:` header value across plain-text and JSON-serialised
 * shapes. Same prefix structure as `X_PAYMENT_HEADER_REGEX`. The value
 * class excludes quotes, braces, CRLF so we don't eat JSON closers.
 */
export const COOKIE_HEADER_REGEX = /(["']?\bcookie["']?\s*[:=]\s*["']?)([^\r\n"'}]+)/gi

/**
 * Placeholder strings substituted in. Distinct prefixes so a reviewer
 * eyeballing a redacted log can tell which sanitiser fired (and so
 * grep'ing the codebase for these strings finds the redactor).
 */
export const REDACTED_BEARER = '[REDACTED-BEARER]'
export const REDACTED_X_PAYMENT = '[REDACTED-X-PAYMENT]'
export const REDACTED_COOKIE = '[REDACTED-COOKIE]'

/**
 * Redact every `Authorization: Bearer <token>` instance in `input`.
 * Returns the input unchanged if no match (no allocation).
 */
export function redactBearer(input: string): string {
  if (input.length === 0) return input
  if (!/bearer/i.test(input)) return input
  return input.replace(BEARER_REGEX, `$1${REDACTED_BEARER}`)
}

/**
 * Redact every payment signature header value in `input`.
 * Returns the input unchanged if no match.
 */
export function redactPaymentHeader(input: string): string {
  if (input.length === 0) return input
  if (!/(?:payment-signature|x-payment)/i.test(input)) return input
  return input.replace(PAYMENT_SIGNATURE_HEADER_REGEX, `$1${REDACTED_X_PAYMENT}`)
}

/**
 * Redact every `Cookie:` header value in `input`.
 * Returns the input unchanged if no match.
 */
export function redactCookies(input: string): string {
  if (input.length === 0) return input
  if (!/cookie/i.test(input)) return input
  return input.replace(COOKIE_HEADER_REGEX, `$1${REDACTED_COOKIE}`)
}

/**
 * Composite: apply all three redactors. Order is bearer -> payment signature
 * -> cookie; each is independent so the order is for diagnostic
 * stability of the output, not correctness.
 */
export function redactSensitiveHeaders(input: string): string {
  return redactCookies(redactPaymentHeader(redactBearer(input)))
}

/**
 * Header names that MUST NEVER be captured into OTel span attributes
 * or log-attribute keys. Used as a positive deny-list by
 * `assertNoSensitiveHeaderCapture` (called at SDK init) and by the
 * companion regression test in
 * `apps/aient-app/tests/x402/redaction-no-bearer-leak.test.ts`.
 *
 * Header names are stored lowercase; the validator lowercases its
 * input before checking, so config maps using either casing are
 * caught.
 */
export const FORBIDDEN_HEADER_NAMES_LOWER = [
  'authorization',
  'payment-signature',
  'x-payment',
  'cookie',
  'set-cookie',
] as const

export type ForbiddenHeaderName = (typeof FORBIDDEN_HEADER_NAMES_LOWER)[number]

/**
 * Defence-in-depth guard: assert that no `attributesFrom{Request,Response}Headers`
 * config in the OTel fetch instrumentation maps a span attribute from
 * any of the forbidden header names. Called at SDK init in
 * `packages/otel/src/sdk.ts`. Throws a descriptive error rather than
 * silently capturing the header.
 *
 * The check is structural — it inspects the values of the
 * `attributesFromRequestHeaders` / `attributesFromResponseHeaders`
 * maps (the headers being captured) regardless of the attribute-name
 * keys.
 */
export function assertNoSensitiveHeaderCapture(
  attributesFromHeaders: Readonly<Record<string, string>> | undefined,
  location: string,
): void {
  if (!attributesFromHeaders) return
  for (const headerName of Object.values(attributesFromHeaders)) {
    const lower = headerName.trim().toLowerCase()
    if (FORBIDDEN_HEADER_NAMES_LOWER.includes(lower as ForbiddenHeaderName)) {
      throw new Error(
        `OTel header-capture config at ${location} maps a span attribute from "${headerName}", `
        + `which is forbidden by the x402 redaction policy (plan §5.3, Sec C1). `
        + `Capturing this header risks leaking session tokens / payment payloads / cookies into traces. `
        + `Remove the mapping; use Aient's request-scoped fields instead.`,
      )
    }
  }
}

/**
 * Redact in-place over the values of an attribute bag (the structured
 * payload OTel logger.emit receives). Walks one level deep and
 * sanitises any string value; nested objects are JSON-serialised,
 * sanitised, then left as a string (a logged attribute that's a
 * sub-object becomes a redacted JSON string — preferable to leaking).
 *
 * Returns a NEW object; the input is not mutated. Numeric and boolean
 * values pass through unchanged.
 */
export function redactLoggerPayload(
  attributes: Readonly<Record<string, unknown>>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(attributes)) {
    if (typeof value === 'string') {
      out[key] = redactSensitiveHeaders(value)
      continue
    }
    if (value === null || typeof value !== 'object') {
      out[key] = value
      continue
    }
    // Nested object — serialise + sanitise. The cost is small because
    // log attributes are typically shallow.
    try {
      const serialised = JSON.stringify(value)
      out[key] = redactSensitiveHeaders(serialised)
    } catch {
      out[key] = '[unserializable]'
    }
  }
  return out
}
